import L, { Layer, Polyline } from "leaflet";
import { useCallback } from "react";
import { LineString, MultiLineString } from "@turf/turf";

interface UseUndoRedoProps {
    map: L.DrawMap | null;
    featureGroup: React.MutableRefObject<L.FeatureGroup<any>>;
    points: React.MutableRefObject<L.LatLng[]>;
    layerOnClick: (e: L.LeafletMouseEvent, layer: L.Polygon<any> | Polyline<LineString | MultiLineString, any> | Layer) => void;
}

export const useUndoRedo = ({ map, featureGroup, points, layerOnClick }: UseUndoRedoProps) => {
    const mapExists = !!map;

    const reattachEventHandlers = useCallback(() => {
        if (!map) {
            return;
        }

        featureGroup.current.eachLayer((layer) => {
            layer.off("click");
            layer.on("click", (e: L.LeafletMouseEvent) => layerOnClick(e, layer));
        });
    }, [map, featureGroup, layerOnClick]);

    const handleUndo = useCallback(() => {
        if (!mapExists) {
            return;
        }

        const visiblePolygonsBeforeUndo: L.Layer[] = [];
        featureGroup.current.eachLayer((layer) => {
            visiblePolygonsBeforeUndo.push(layer);
        });

        map.eachLayer((layer: any) => {
            if (layer instanceof L.CircleMarker) {
                map.removeLayer(layer);
            }
            if (layer instanceof L.Polyline && (layer.options?.dashArray || layer.options?.className?.includes("temp-drawing"))) {
                map.removeLayer(layer);
            }
        });

        points.current = [];

        const event = new KeyboardEvent("keydown", { key: "z", ctrlKey: true });
        document.dispatchEvent(event);

        setTimeout(reattachEventHandlers, 150);
    }, [featureGroup, map, mapExists, points, reattachEventHandlers]);

    const handleRedo = useCallback(() => {
        if (!map) {
            return;
        }

        const visiblePolygonsBeforeRedo: L.Layer[] = [];
        featureGroup.current.eachLayer((layer) => {
            visiblePolygonsBeforeRedo.push(layer);
        });

        map.eachLayer((layer: any) => {
            if (layer instanceof L.CircleMarker) {
                map.removeLayer(layer);
            }
            if (layer instanceof L.Polyline && (layer.options?.dashArray || layer.options?.className?.includes("temp-drawing"))) {
                map.removeLayer(layer);
            }
        });

        points.current = [];

        const event = new KeyboardEvent("keydown", { key: "z", ctrlKey: true, shiftKey: true });
        document.dispatchEvent(event);

        setTimeout(reattachEventHandlers, 150);
    }, [featureGroup, map, points, reattachEventHandlers]);

    return { handleUndo, handleRedo };
};
